import {clearModalHistoryEntry} from '@Components/modal-container/modal-history-middleware';
import {authenticateUser, isUserLoggedIn, isUserPremium} from '@Libraries/user.library';
import {replaceSignupFormEditorUrlInPlace, replaceWithSignupFormPublishWizard} from '@Libraries/email-signup-form-library';
import {noop} from '@Utils/general.util';
import type {FormMakerImmutableStore} from '@FormMakerReact/form-maker.types';

const AUTHENTICATE_SIGNUP_SOURCE = 'signup-form-editor';

const EMAIL_SIGNUP_FORM_MARKETING_PAGE_PATH = 'm/email-signup-form';

/** A form the backend knows about. The anonymous draft editor carries an empty id until its first save creates the row. */
export function hasPersistedForm(store: Pick<FormMakerImmutableStore, 'idEmailSignupForm'>): boolean {
  return store.idEmailSignupForm !== '';
}

/**
 * A soft save minted the draft's real form: swap the id-less URL for load/{id} in place so the same
 * editor keeps running with autosave on — no reload. `clearModalHistoryEntry()` because this often
 * runs from the login modal's success callback, and the modal container's deferred `history.back()`
 * would otherwise navigate away from the editor right after the swap.
 */
export function adoptDraftInPlace(idEmailSignupForm: string): void {
  clearModalHistoryEntry();
  replaceSignupFormEditorUrlInPlace(idEmailSignupForm);
}

/**
 * A Publish minted the draft's real form: leave the id-less editor for the publish wizard (a separate
 * page). Replaced in history — Back to the id-less draft would save as a second form. Same modal-entry
 * clearing as `adoptDraftInPlace`.
 */
export function leaveDraftForPublishWizard(idEmailSignupForm: string): void {
  clearModalHistoryEntry();
  replaceWithSignupFormPublishWizard(idEmailSignupForm);
}

/**
 * Save gate: true when the caller may persist. Anonymous callers get the auth modal, whose success
 * replays the save via `onAuthenticated` (re-entering here, so a free plan then hits the upsell).
 */
export function ensureCanSaveSignupForm(onAuthenticated: VoidFunction): boolean {
  if (!isUserLoggedIn()) {
    void authenticateUser(onAuthenticated, noop, {
      signupSource: AUTHENTICATE_SIGNUP_SOURCE,
      panelText: window.i18next.t('pmwjs_signup_form_login_to_save'),
      panelSubtext: window.i18next.t('pmwjs_signup_form_login_to_save_subtext'),
    });
    return false;
  }

  if (!isUserPremium()) {
    void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_SIGNUP_FORM);
    return false;
  }

  return true;
}

/** Back arrow and logo: a draft has no My Stuff entry to return to, so it goes to the marketing page instead. */
export function navigateBackFromSignupFormEditor(store: Pick<FormMakerImmutableStore, 'idEmailSignupForm'>): void {
  if (hasPersistedForm(store)) {
    window.history.back();
    return;
  }
  window.location.assign(window.PMW.util.site_url(EMAIL_SIGNUP_FORM_MARKETING_PAGE_PATH));
}
